import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { TableColumn } from '../../atoms/table/table.model';
import { TableComponent } from '../../atoms/table/table.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDatePipe, TranslocoDecimalPipe, } from '@jsverse/transloco-locale';
import { ALARMS_TABLE_DEFAULT_COLUMNS } from '../../../constants/table.constants';
import { AlarmDetails } from '@data';
import { PriorityIndicatorComponent } from '../../molecules/indicators/priority-badge-indicator.component';
import { PhaseIndicatorComponent } from '../../molecules/indicators/phase-indicator.component';
import { BadgeIndicatorComponent } from '../../molecules/indicators/badge-indicator.component';
import { phase, Unit } from '../../../models/units.models';
import { PHASE, UNITS } from '../../../constants/units.constants';
import { DurationIndicatorComponent } from '../../molecules/indicators/duration-indicator.component';
import { AlarmCenterIndicatorComponent } from '../../molecules/indicators/alarm-center-indicator.component';
import { ActionsIndicatorComponent } from '../../molecules/indicators/actions-indicator.component';
import { IndicatorAction } from '@ui';
import { AlarmCategoryIndicatorComponent } from '../../molecules/indicators/alarm-category-indicator.component';
import { PermissionsService } from '../../../../app/core/services/permissions.service';

@Component({
  selector: 'ui-alarms-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TableComponent,
    TranslocoDirective,
    TranslocoDecimalPipe,
    PriorityIndicatorComponent,
    PhaseIndicatorComponent,
    BadgeIndicatorComponent,
    TranslocoDatePipe,
    DurationIndicatorComponent,
    AlarmCenterIndicatorComponent,
    ActionsIndicatorComponent,
    AlarmCategoryIndicatorComponent,
  ],
  template: `
    <ng-container *transloco="let t">
      @if (dataSource() && columns().length) {
        <ui-table
          [dataSource]="dataSource()"
          [columns]="columns()"
          [columnTemplates]="columnTemplates()"
          [scrollConfig]="scrollConfig()"
          [pageSize]="pageSize()"
          [pageIndex]="pageIndex()"
          [showPagination]="showPagination()"
          [showPageSizeChanger]="showPageSizeChanger()"
          [total]="total()"
          [showTotal]="total() ? totalTemplate : null"
          [loading]="loading()"
          [frontPagination]="false"
          [isMultiSelect]="true"
          (onQueryParams)="onQueryParamsChange($event)"
          (onChecked)="handleChecked($event)"
        />
      }

      <ng-template #centerTemplate let-row="row">
        <ui-alarm-center-indicator
          [state]="row.dateEnd !== null ? 'active' : 'danger'"
          [name]="row.centerName"
          [centerId]="row.centerId"
          [distributorId]="row.distributorId"
          [rsabtId]="row.rsabtId"
          [tsabtId]="row.tsabtId"
          [vtnId]="row.vtnId"
          (onCenter)="emitCenter($event)"
        />
      </ng-template>
      <ng-template #lineTemplate let-row="row">
        <ui-alarm-center-indicator
          [state]="row.dateEnd !== null ? 'active' : 'danger'"
          [name]="row.line_name"
          [centerId]="row.centerId"
          [lineId]="row.line_id"
          [distributorId]="row.distributorId"
          [rsabtId]="row.rsabtId"
          [tsabtId]="row.tsabtId"
          [vtnId]="row.vtnId"
        />
      </ng-template>
      <ng-template #categoryTemplate let-row="row">
        <ui-alarm-category-indicator
          [category]="row.alarmCategory"
          [type]="row.alarmType"
        />
      </ng-template>
      <ng-template #priorityTemplate let-row="row">
        <div class="mx-auto w-fit">
          <ui-priority-indicator [priority]="row.priority" />
        </div>
      </ng-template>
      <ng-template #phaseTemplate let-row="row">
        <div class="mx-auto w-fit">
          <ui-phase-indicator [phase]="getPhase(row.field.phase)" />
        </div>
      </ng-template>
      <ng-template #fieldTemplate let-row="row">
        <div class="ml-auto">
          <ui-badge-indicator
            [value]="row.field.value"
            [nominal]="row.field.reference_value"
            [percentage]="row.field.percentage"
            [units]="getUnit(row.field.unit)"
            [unitsReference]="getUnit(row.field.reference_value_unit)"
            [tooltipLabel]="'ALARMS.REF_VALUE'"
          />
        </div>
      </ng-template>
      <ng-template #startTemplate let-row="row">
        {{
          row.dateInit
            ? (row.dateInit
              | translocoDate: { dateStyle: 'short', timeStyle: 'short' })
            : '--'
        }}
      </ng-template>
      <ng-template #durationTemplate let-row="row">
        <ui-duration-indicator
          [dateInit]="row.dateInit"
          [dateEnd]="row.dateEnd"
        />
      </ng-template>
      <ng-template #actionsTemplate let-row="row">
        @if (hasWritePermission()) {
          <div class="flex justify-end">
            <ui-actions-indicator
              (onAction)="emitAction(row)"
              [actions]="{
                archive: {
                  disabled: getActiveState(row.dateInit, row.dateEnd, row.read),
                },
              }"
            />
          </div>
        }
      </ng-template>
      <ng-template #totalTemplate>
        {{ total() | translocoDecimal }}
        {{ t('DISTRIBUTION_NETWORK.TABLE.RESULTS') }}
      </ng-template>
    </ng-container>
  `,
})
export class AlarmsTableComponent {
  private translocoService = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);
  protected readonly permissionsService = inject(PermissionsService);

  data = input.required<AlarmDetails[]>();
  total = input<number>(100);
  loading = input<boolean>(false);
  pageSize = input<number>(10);
  pageIndex = input<number>(1);
  showPagination = input<boolean>(true);
  showPageSizeChanger = input<boolean>(true);
  showCenterCode = input<boolean>(false);

  onQueryParams = output<{
    orderBy: string | undefined;
    order: string | undefined;
    pageSize: number;
    pageIndex: number;
  }>();
  onCenter = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
  }>();
  onCopyCenterId = output<string>();
  onRsabtId = output<string>();
  onAction = output<IndicatorAction>();
  onChecked = output<AlarmDetails[]>();

  actions = signal<Record<any, { disabled: boolean }>>({
    archive: { disabled: false },
  });

  // use a stable key for each row so rows/components are recreated on pagination changes
  dataSource = computed(() =>
    this.data().map((item) => {
      // try to use an explicit id if backend provides it
      const stableId =
        (item as any).id ??
        // fallback to a combination of stable fields (adjust fields to your model)
        `${item.alarmId}`;
      return { id: stableId, ...item };
    }),
  );

  centerTemplate = viewChild.required<TemplateRef<any>>('centerTemplate');
  lineTemplate = viewChild.required<TemplateRef<any>>('lineTemplate');
  categoryTemplate = viewChild.required<TemplateRef<any>>('categoryTemplate');
  priorityTemplate = viewChild.required<TemplateRef<any>>('priorityTemplate');
  phaseTemplate = viewChild.required<TemplateRef<any>>('phaseTemplate');
  fieldTemplate = viewChild.required<TemplateRef<any>>('fieldTemplate');
  startTemplate = viewChild.required<TemplateRef<any>>('startTemplate');
  durationTemplate = viewChild.required<TemplateRef<any>>('durationTemplate');
  actionsTemplate = viewChild.required<TemplateRef<any>>('actionsTemplate');

  columnTemplates = computed(() => ({
    center: this.centerTemplate(),
    line: this.lineTemplate(),
    category: this.categoryTemplate(),
    priority: this.priorityTemplate(),
    phase: this.phaseTemplate(),
    field: this.fieldTemplate(),
    start: this.startTemplate(),
    duration: this.durationTemplate(),
    actions: this.actionsTemplate(),
  }));

  columns = signal<TableColumn[]>([]);
  scrollConfigDefault = signal<{ x?: string | null; y?: string | null }>({
    x: '1000px',
    y: 'calc(100vh - 350px)',
  });
  scrollConfig = computed<{ x?: string | null; y?: string | null }>(() =>
    this.data()?.length ? this.scrollConfigDefault() : {},
  );
  DEFAULT_COLUMNS = computed<TableColumn[]>(() => {
    let cols = ALARMS_TABLE_DEFAULT_COLUMNS;
    if (!this.hasWritePermission()) {
      cols = cols.filter((col) => col.key !== 'actions');
    }
    return cols;
  });
  permissions = this.permissionsService.permissions;

  /**
   * Initializes the AlarmsTableComponent and sets up column localization.
   */
  constructor() {
    effect(() => {
      this.setLocalizeColumns();
    });
  }

  /**
   * Returns true if the user has 'w' write permission.
   */
  hasWritePermission(): boolean {
    return this.permissions().some((p) => p === 'sabt:alarms:w');
  }

  /**
   * Sets the localized column titles using the current translation.
   */
  setLocalizeColumns() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.columns.set(
          this.DEFAULT_COLUMNS().map((col) => ({
            ...col,
            title: this.translocoService.translate(col.title),
          })),
        );
      });
  }

  /**
   * Emits the selected RSABT ID.
   * @param id - The RSABT ID to emit.
   */
  emitRsabtId(id: string) {
    this.onRsabtId.emit(id);
  }

  /**
   * Handles changes to table query parameters and emits them.
   * @param params - The table query parameters.
   */
  onQueryParamsChange(params: NzTableQueryParams): void {
    const { sort, pageSize, pageIndex } = params;
    const currentSort = sort.find((item) => item.value !== null);
    const orderBy = (currentSort && currentSort.key) || undefined;
    const order = (currentSort && currentSort.value) || undefined;
    this.onQueryParams.emit({ orderBy, order, pageSize, pageIndex });
  }

  emitLine() {}

  /**
   * Emits the selected center event.
   * @param event - The center event object containing IDs.
   */
  emitCenter(event: {
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
  }) {
    this.onCenter.emit(event);
  }

  /**
   * Returns the unit label for a given unit key.
   * If the mapping has a value it is returned; otherwise '%' is returned.
   * @param unit - The unit key.
   * @returns The unit label or '%'
   */
  getUnit(unit: Unit) {
    const label = UNITS[unit];
    return label ? label : '%';
  }

  getPhase(p: phase | null) {
    return p != null ? PHASE[p] : '';
  }

  /**
   * Returns the actions object for a table item, setting the archive action's disabled state.
   * @param disabled - Whether the archive action should be disabled.
   * @returns The actions object.
   */
  getItemActions(disabled: boolean): Record<any, { disabled: boolean }> {
    return { archive: { disabled } };
  }

  /**
   * Emits the action event for a table row.
   * @param action - The indicator action object.
   */
  emitAction(action: IndicatorAction) {
    this.onAction.emit(action);
  }

  /**
   * Emits the checked row items based on their indices.
   * @param indexs - Array of checked row indices.
   */
  handleChecked(indexs: any[]) {
    const items: AlarmDetails[] = this.data().filter((item) =>
      indexs.includes(item.alarmId),
    );
    this.onChecked.emit(items);
  }

  /**
   * Returns the active state based on the alarm's start, end, and read status.
   * @param dateInit - The start date of the alarm.
   * @param dateEnd - The end date of the alarm.
   * @param read - Boolean indicating if the alarm is read.
   * @returns True if active, false otherwise.
   */
  getActiveState(dateInit: Date, dateEnd: Date, read: boolean) {
    const initTime = new Date(dateInit)?.getTime();
    const endTime = new Date(dateEnd)?.getTime();
    return !!((!endTime && initTime) || read);
  }
}
